// Migration 0047 — TanStack Query mutation for POST /api/v1/account/free-claim.
//
// Mints the weekly free sealed dossier from a random live drop for the
// authenticated user. Idempotent within a window — a second POST returns
// state "claimed" WITHOUT minting.
//
// onSuccess invalidates ["free-claim"] (so the shop slot flips to the
// countdown bar + the nav dot goes dark) and ["vault-items"] (so the newly
// minted sealed dossier appears in the user's collection without a reload).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { FreeClaimEnvelope } from "./useFreeClaim";

export interface ClaimFreeDossierError {
  code: string;
  message: string;
}

async function postClaim(): Promise<FreeClaimEnvelope> {
  const response = await fetch("/api/v1/account/free-claim", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
    },
    body: "{}",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ClaimFreeDossierError = {
      code: "FREE_CLAIM_FAILED",
      message: `Free claim failed (HTTP ${response.status})`,
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as FreeClaimEnvelope;
}

export function useClaimFreeDossier() {
  const queryClient = useQueryClient();
  return useMutation<FreeClaimEnvelope, ClaimFreeDossierError, void>({
    mutationFn: postClaim,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["free-claim"] });
      // The minted sealed dossier lands in the user's collection.
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    },
  });
}
